import React, { useState, useEffect } from 'react';
import './ProfitShare.css';

const ProfitShare = () => {
  // Helper function to get API base URL
  const getApiBaseUrl = () => {
    if (window.location.hostname === 'mgmt.storytimechess.com' || 
        window.location.hostname === 'admin.storytimechess.com' ||
        window.location.hostname === 'thinking-cup.com' ||
        window.location.hostname === 'www.thinking-cup.com') {
      return 'https://tcinc-dashboard.onrender.com/api';
    }
    return '/api';
  };
  
  // Helper functions for persistent month/year selection
  const getSavedMonthYear = () => {
    const saved = localStorage.getItem('profitShareMonthYear');
    if (!saved) return null;

    try {
      const { year, month, timestamp } = JSON.parse(saved);
      const now = new Date().getTime();
      const savedTime = new Date(timestamp).getTime();
      const hoursSinceSaved = (now - savedTime) / (1000 * 60 * 60);

      // If saved more than 4 hours ago, reset to current month
      if (hoursSinceSaved > 4) {
        localStorage.removeItem('profitShareMonthYear');
        return null;
      }

      return { year, month };
    } catch (e) {
      localStorage.removeItem('profitShareMonthYear');
      return null;
    }
  };

  const saveMonthYear = (year, month) => {
    const data = {
      year,
      month,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('profitShareMonthYear', JSON.stringify(data));
  };

  // Initialize with saved values or previous month
  const savedMonthYear = getSavedMonthYear();
  const getDefaultPreviousMonth = () => {
    const now = new Date();
    const currentMonth = now.getMonth(); // 0-indexed: Jan=0, Feb=1, etc.
    if (currentMonth === 0) {
      // January → default to December of previous year
      return { year: now.getFullYear() - 1, month: 12 };
    }
    return { year: now.getFullYear(), month: currentMonth }; // currentMonth is already previous month (0-indexed)
  };
  const defaultMonthYear = getDefaultPreviousMonth();
  const [selectedYear, setSelectedYear] = useState(savedMonthYear?.year || defaultMonthYear.year);
  const [selectedMonth, setSelectedMonth] = useState(savedMonthYear?.month || defaultMonthYear.month);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profitShareData, setProfitShareData] = useState(null);
  const [config, setConfig] = useState(null);
  
  // Club profit share states
  const [activeTab, setActiveTab] = useState('california'); // 'california', 'park-slope', 'ues', 'travel-stipend'
  const [clubData, setClubData] = useState({
    'park-slope': null,
    'ues': null
  });
  const [clubLoading, setClubLoading] = useState(false);
  
  // Travel stipend states
  const [travelStipendData, setTravelStipendData] = useState(null);
  const [travelStipendLoading, setTravelStipendLoading] = useState(false);
  const [showTravelStipendLessonModal, setShowTravelStipendLessonModal] = useState(false);
  const [selectedTravelStipendLessons, setSelectedTravelStipendLessons] = useState(null);
  const [selectedTravelStipendTutor, setSelectedTravelStipendTutor] = useState(null);
  const [selectedTravelStipendWeek, setSelectedTravelStipendWeek] = useState(null);
  
  // Modal states
  const [showTutorModal, setShowTutorModal] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [selectedTutorMarket, setSelectedTutorMarket] = useState('');
  const [showAddTutorModal, setShowAddTutorModal] = useState(false);
  const [addTutorMarket, setAddTutorMarket] = useState('');
  const [tutorSearchQuery, setTutorSearchQuery] = useState('');
  const [tutorSearchResults, setTutorSearchResults] = useState([]);
  const [showCreateManualTutor, setShowCreateManualTutor] = useState(false);
  const [manualTutorForm, setManualTutorForm] = useState({ firstName: '', lastName: '', email: '' });
  const [adjustmentForm, setAdjustmentForm] = useState({ amount: '', note: '', date: '' });
  
  // Club lesson modal states
  const [showClubLessonModal, setShowClubLessonModal] = useState(false);
  const [selectedClubLessons, setSelectedClubLessons] = useState(null);
  const [selectedClubType, setSelectedClubType] = useState(''); // 'lessons' or 'support'
  
  // Ref for search input autofocus
  const searchInputRef = React.useRef(null);
  
  // Office hours & mileage modal states
  const [showOfficeHoursModal, setShowOfficeHoursModal] = useState(false);
  const [showMileageModal, setShowMileageModal] = useState(false);
  const [showSoftwareLicenseModal, setShowSoftwareLicenseModal] = useState(false);
  const [showSchoolRevenueModal, setShowSchoolRevenueModal] = useState(false);
  const [showTutorRevenueModal, setShowTutorRevenueModal] = useState(false);
  const [editingOfficeHoursIndex, setEditingOfficeHoursIndex] = useState(null);
  const [editingMileageIndex, setEditingMileageIndex] = useState(null);
  const [editingSoftwareLicenseIndex, setEditingSoftwareLicenseIndex] = useState(null);
  const [editingSchoolRevenueIndex, setEditingSchoolRevenueIndex] = useState(null);
  const [editingTutorRevenueIndex, setEditingTutorRevenueIndex] = useState(null);
  const [editingPayAdjustmentIndex, setEditingPayAdjustmentIndex] = useState(null);
  const [editingRevenueAdjustmentIndex, setEditingRevenueAdjustmentIndex] = useState(null);
  
  // Settings modal
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  // Form states
  const [officeHoursForm, setOfficeHoursForm] = useState({
    tutorId: '',
    tutorName: '',
    hours: '',
    rate: '25',
    description: ''
  });
  
  const [mileageForm, setMileageForm] = useState({
    tutorId: '',
    tutorName: '',
    amount: '',
    description: ''
  });

  const [softwareLicenseForm, setSoftwareLicenseForm] = useState({
    amount: '',
    description: ''
  });

  const [schoolRevenueForm, setSchoolRevenueForm] = useState({
    tutorId: '',
    tutorName: '',
    amount: '',
    description: '',
    market: 'LA'
  });

  const [tutorRevenueForm, setTutorRevenueForm] = useState({
    amount: '',
    note: '',
    date: ''
  });

  const [settingsForm, setSettingsForm] = useState({
    laAdSpend: 0,
    sfAdSpend: 0,
    caitlinSalary: 4167,
    profitSharePercentage: 20,
    notes: ''
  });
  
  const years = [2024, 2025, 2026];
  const months = [
    { value: 0, label: 'Annual/YTD' },
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];
  
  const [annualData, setAnnualData] = useState(null);
  const isAnnualView = selectedMonth === 0;
  
  useEffect(() => {
    if (activeTab === 'california') {
      fetchProfitShareData();
    } else if (activeTab === 'travel-stipend') {
      fetchTravelStipendData();
    } else {
      fetchClubData(activeTab);
    }
  }, [selectedYear, selectedMonth, activeTab]);

  // Fetch club data when tab changes
  useEffect(() => {
    if (activeTab !== 'california' && activeTab !== 'travel-stipend' && !clubData[activeTab]) {
      fetchClubData(activeTab);
    }
  }, [activeTab]);

  // Monitor token changes to reset to current month on login (but preserve user selections)
  useEffect(() => {
    const token = localStorage.getItem('token');
    const lastTokenCheck = localStorage.getItem('lastTokenCheck');

    // Only check when token first appears (user logs in) or after a long time
    if (token && (!lastTokenCheck || Date.now() - parseInt(lastTokenCheck) > 60000)) {
      const savedMonthYear = getSavedMonthYear();
      if (savedMonthYear) {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;

        // Only reset if the saved selection is from a different YEAR than current
        // Don't reset just because it's a different month - users should be able to view historical data
        if (savedMonthYear.year !== currentYear) {
          setSelectedYear(currentYear);
          setSelectedMonth(currentMonth);
          saveMonthYear(currentYear, currentMonth);
        }
        // If it's just a different month in the same year, keep the user's selection
      }
      localStorage.setItem('lastTokenCheck', Date.now().toString());
    }
  }, []); // Only run once on mount
  
  const fetchProfitShareData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      // If Annual/YTD view is selected, fetch annual data for LA market
      if (isAnnualView) {
        const annualResponse = await fetch(
          `/api/profit-share/annual/${selectedYear}/LA`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (!annualResponse.ok) {
          throw new Error('Failed to fetch annual profit share data');
        }
        
        const annualData = await annualResponse.json();
        setAnnualData(annualData.data);
        setProfitShareData(null);
        setConfig(null);
        return;
      }
      
      // Otherwise, fetch monthly data as before
      setAnnualData(null);
      
      // Fetch config
      const configResponse = await fetch(
        `/api/profit-share/${selectedYear}/${selectedMonth}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!configResponse.ok) {
        throw new Error('Failed to fetch profit share configuration');
      }
      
      const configData = await configResponse.json();
      setConfig(configData.data);
      
      // Update settings form with config values
      setSettingsForm({
        laAdSpend: configData.data.laAdSpend || 0,
        sfAdSpend: configData.data.sfAdSpend || 0,
        caitlinSalary: configData.data.caitlinSalary || 4167,
        profitSharePercentage: configData.data.profitSharePercentage || 20,
        notes: configData.data.notes || ''
      });
      
      // Fetch calculations
      const calcResponse = await fetch(
        `/api/profit-share/${selectedYear}/${selectedMonth}/calculate`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!calcResponse.ok) {
        throw new Error('Failed to fetch profit share calculations');
      }
      
      const calcData = await calcResponse.json();
      setProfitShareData(calcData.data);
      
    } catch (err) {
      console.error('Error fetching profit share data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchClubData = async (club) => {
    try {
      setClubLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const response = await fetch(
        `/api/profit-share/clubs/${selectedYear}/${selectedMonth}/${club}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch ${club} club data`);
      }
      
      const data = await response.json();
      setClubData(prev => ({
        ...prev,
        [club]: data.data
      }));
      
    } catch (err) {
      console.error(`Error fetching ${club} club data:`, err);
      setError(err.message);
    } finally {
      setClubLoading(false);
    }
  };
  
  const fetchTravelStipendData = async () => {
    try {
      setTravelStipendLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const response = await fetch(
        `/api/profit-share/${selectedYear}/${selectedMonth}/travel-stipend`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch travel stipend data');
      }
      
      const data = await response.json();
      setTravelStipendData(data.data);
      
    } catch (err) {
      console.error('Error fetching travel stipend data:', err);
      setError(err.message);
    } finally {
      setTravelStipendLoading(false);
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  const handleCreateManualTutor = async () => {
    if (!manualTutorForm.firstName || !manualTutorForm.lastName) {
      alert('First name and last name are required');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const apiBaseUrl = getApiBaseUrl();
      
      const response = await fetch(
        `${apiBaseUrl}/profit-share/tutors/create-manual`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(manualTutorForm)
        }
      );
      
      // Check if response is JSON before trying to parse
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Server returned an unexpected response. Please check the server logs.');
      }
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create manual tutor');
      }
      
      const data = await response.json();
      
      // Add the newly created tutor to the market
      await handleAddTutor(
        data.data.contractorId,
        `${data.data.firstName} ${data.data.lastName}`,
        addTutorMarket
      );
      
      // Reset form
      setManualTutorForm({ firstName: '', lastName: '', email: '' });
      setShowCreateManualTutor(false);
    } catch (err) {
      console.error('Error creating manual tutor:', err);
      alert(err.message || 'Failed to create manual tutor');
    }
  };
  
  const handleAddTutor = async (tutorId, tutorName, market) => {
    try {
      const token = localStorage.getItem('token');
      const apiBaseUrl = getApiBaseUrl();
      const updatedIds = market === 'LA' 
        ? [...(config.laTutorIds || []), tutorId]
        : [...(config.sfTutorIds || []), tutorId];
      
      const response = await fetch(
        `${apiBaseUrl}/profit-share/${selectedYear}/${selectedMonth}/tutors`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            [market === 'LA' ? 'laTutorIds' : 'sfTutorIds']: updatedIds
          })
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to add tutor');
      }
      
      await fetchProfitShareData();
      setShowAddTutorModal(false);
      setTutorSearchQuery('');
      setTutorSearchResults([]);
      setShowCreateManualTutor(false);
    } catch (err) {
      console.error('Error adding tutor:', err);
      alert('Failed to add tutor');
    }
  };
  
  const handleRemoveTutor = async (tutorId, market) => {
    if (!window.confirm('Are you sure you want to remove this tutor from the profit share calculation?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const updatedIds = market === 'LA'
        ? config.laTutorIds.filter(id => id !== tutorId)
        : config.sfTutorIds.filter(id => id !== tutorId);
      
      const response = await fetch(
        `/api/profit-share/${selectedYear}/${selectedMonth}/tutors`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            [market === 'LA' ? 'laTutorIds' : 'sfTutorIds']: updatedIds
          })
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to remove tutor');
      }
      
      await fetchProfitShareData();
    } catch (err) {
      console.error('Error removing tutor:', err);
      alert('Failed to remove tutor');
    }
  };
  
  const handleImportFromPreviousMonth = async () => {
    // Calculate previous month
    let sourceYear = selectedYear;
    let sourceMonth = selectedMonth - 1;
    
    if (sourceMonth < 1) {
      sourceMonth = 12;
      sourceYear = selectedYear - 1;
    }
    
    const sourceMonthName = new Date(sourceYear, sourceMonth - 1).toLocaleString('default', { month: 'long' });
    const currentMonthName = new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long' });
    
    if (!window.confirm(
      `Import tutors from ${sourceMonthName} ${sourceYear} to ${currentMonthName} ${selectedYear}?\n\n` +
      `This will replace all current tutors in ${currentMonthName} with tutors from ${sourceMonthName}.`
    )) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const apiBaseUrl = getApiBaseUrl();
      
      const response = await fetch(
        `${apiBaseUrl}/profit-share/${selectedYear}/${selectedMonth}/import-from/${sourceYear}/${sourceMonth}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to import tutors');
      }
      
      const data = await response.json();
      alert(
        `Successfully imported ${data.data.imported.laTutorIds} LA tutors and ${data.data.imported.sfTutorIds} SF tutors from ${sourceMonthName} ${sourceYear}!`
      );
      
      // Refresh the data
      await fetchProfitShareData();
    } catch (err) {
      console.error('Error importing tutors:', err);
      alert(`Failed to import tutors: ${err.message}`);
    }
  };
  
  const handleSearchTutors = async () => {
    if (tutorSearchQuery.length < 2) {
      setTutorSearchResults([]);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const apiBaseUrl = getApiBaseUrl();
      
      const response = await fetch(
        `${apiBaseUrl}/profit-share/tutors/search?query=${encodeURIComponent(tutorSearchQuery)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to search tutors');
      }
      
      const data = await response.json();
      setTutorSearchResults(data.data || []);
    } catch (err) {
      console.error('Error searching tutors:', err);
    }
  };
  
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (tutorSearchQuery && tutorSearchQuery.length >= 2 && showAddTutorModal) {
        handleSearchTutors();
      } else if (tutorSearchQuery.length < 2) {
        setTutorSearchResults([]);
      }
    }, 300);
    
    return () => clearTimeout(delayDebounceFn);
  }, [tutorSearchQuery, showAddTutorModal]);
  
  // Auto-focus search input when Add Tutor modal opens
  useEffect(() => {
    if (showAddTutorModal && searchInputRef.current) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [showAddTutorModal]);
  
  const handleViewTutorLessons = (tutor, market) => {
    // Get tutor data from calculations (includes appointments, revenue, pay, and adjustments)
    const tutorData = { ...tutor, market };
    setSelectedTutor(tutorData);
    setSelectedTutorMarket(market);

    // Initialize forms - always start fresh
    setAdjustmentForm({ amount: '', note: '', date: '' });
    setTutorRevenueForm({ amount: '', note: '', date: '' });

    setShowTutorModal(true);
  };

  const handleViewClubLessons = (lessons, type, clubName) => {
    setSelectedClubLessons({ lessons, clubName, type });
    setSelectedClubType(type);
    setShowClubLessonModal(true);
  };

  const handleViewTravelStipendLessons = (tutor, week) => {
    setSelectedTravelStipendLessons(week.lessons);
    setSelectedTravelStipendTutor(tutor.tutorName);
    setSelectedTravelStipendWeek({
      weekStart: week.weekStart,
      weekEnd: week.weekEnd,
      hours: week.hours,
      stipend: week.stipend
    });
    setShowTravelStipendLessonModal(true);
  };

  const downloadClubCSV = (lessons, type, clubName) => {
    const headers = [
      'Date',
      'Time',
      'Appointment Topic',
      'Tutor(s)',
      'Lesson ID',
      'Service',
      'Revenue',
      'Tutor Pay'
    ];

    const csvContent = [
      headers.join(','),
      ...lessons.map(lesson => [
        new Date(lesson.start).toLocaleDateString(),
        new Date(lesson.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        `"${(lesson.topic || '').replace(/"/g, '""')}"`,
        `"${(lesson.tutorNames || []).join(', ').replace(/"/g, '""')}"`,
        lesson.appointmentId || '',
        `"${(lesson.service_name || '').replace(/"/g, '""')}"`,
        lesson.revenue || 0,
        lesson.tutorPay || 0
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${clubName.replace(/\s+/g, '_')}_${type}_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadTutorLessonsCSV = (tutor) => {
    if (!tutor || !tutor.appointments || tutor.appointments.length === 0) {
      alert('No lessons to download');
      return;
    }

    const headers = [
      'Lesson',
      'Job Name',
      'Date',
      'Hours',
      'Revenue',
      'Tutor',
      'Tutor Pay',
      'Labels'
    ];

    const csvRows = [
      headers.join(',')
    ];

    tutor.appointments.forEach(apt => {
      // Calculate hours - default to 1 if not available
      let hours = 1;
      // Check if units is available in the appointment
      if (apt.units !== undefined && apt.units !== null) {
        hours = apt.units;
      } else if (apt.start && apt.finish) {
        const durationMs = new Date(apt.finish) - new Date(apt.start);
        hours = Math.round((durationMs / (1000 * 60 * 60)) * 100) / 100;
        // If calculated hours is 0 or invalid, default to 1
        if (!hours || hours <= 0) {
          hours = 1;
        }
      }

      // Format date as mm/dd/yyyy
      const date = new Date(apt.start);
      const formattedDate = `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;

      // Get labels - try multiple sources
      let labels = '';
      if (apt.labels && Array.isArray(apt.labels) && apt.labels.length > 0) {
        labels = apt.labels.map(label => (typeof label === 'string' ? label : label.name || '')).filter(l => l).join(', ');
      } else if (apt.service?.labels && Array.isArray(apt.service.labels) && apt.service.labels.length > 0) {
        labels = apt.service.labels.map(label => (typeof label === 'string' ? label : label.name || '')).filter(l => l).join(', ');
      }

      // Escape quotes in text fields
      const escapeCSV = (text) => {
        if (text === null || text === undefined) return '';
        const str = String(text);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const row = [
        apt.appointmentId || '',
        escapeCSV(apt.topic || ''),
        formattedDate,
        hours,
        (apt.revenue || 0).toFixed(2),
        escapeCSV(tutor.tutorName || ''),
        (apt.tutorPay || 0).toFixed(2),
        escapeCSV(labels)
      ];

      csvRows.push(row.join(','));
    });

    // Add totals row
    csvRows.push([
      '',
      '',
      '',
      '',
      (tutor.totalRevenue || 0).toFixed(2),
      '',
      (tutor.totalTutorPay || 0).toFixed(2),
      ''
    ].join(','));

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const tutorName = (tutor.tutorName || 'Tutor').replace(/[^a-z0-9]/gi, '_');
    link.setAttribute('download', `${tutorName}_Lessons_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadCombinedClubCSV = (clubData, clubName) => {
    const headers = [
      'Type',
      'Date',
      'Time',
      'Appointment Topic',
      'Tutor(s)',
      'Lesson ID',
      'Service',
      'Revenue',
      'Tutor Pay'
    ];

    // Combine lessons and support data
    const combinedData = [
      ...clubData.metrics.lessons.map(lesson => ({
        type: 'Lesson',
        ...lesson
      })),
      ...clubData.metrics.supportAppointments.map(support => ({
        type: 'Support',
        ...support
      }))
    ];

    const csvContent = [
      headers.join(','),
      ...combinedData.map(entry => [
        entry.type,
        new Date(entry.start).toLocaleDateString(),
        new Date(entry.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        `"${(entry.topic || '').replace(/"/g, '""')}"`,
        `"${(entry.tutorNames || []).join(', ').replace(/"/g, '""')}"`,
        entry.appointmentId || '',
        `"${(entry.service_name || '').replace(/"/g, '""')}"`,
        entry.revenue || 0,
        entry.tutorPay || 0
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${clubName.replace(/\s+/g, '_')}_Combined_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleEditPayAdjustment = (index) => {
    const adjustment = selectedTutor.payAdjustments[index];
    setAdjustmentForm({
      amount: adjustment.amount.toString(),
      note: adjustment.note || '',
      date: adjustment.date ? new Date(adjustment.date).toISOString().split('T')[0] : ''
    });
    setEditingPayAdjustmentIndex(index);
  };

  const handleAddAdjustment = async () => {
    if (!adjustmentForm.amount || parseFloat(adjustmentForm.amount) === 0) {
      alert('Please enter a non-zero amount');
      return;
    }

    if (!adjustmentForm.date) {
      alert('Please select a date for this adjustment');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // If editing, delete old and add new
      if (editingPayAdjustmentIndex !== null) {
        // Delete the old adjustment
        await fetch(
          `/api/profit-share/${selectedYear}/${selectedMonth}/tutor-adjustment/${selectedTutor.tutorId}/${selectedTutorMarket}/${editingPayAdjustmentIndex}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
      }
      
      // Add the new/updated adjustment
      const response = await fetch(
        `/api/profit-share/${selectedYear}/${selectedMonth}/tutor-adjustment`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            tutorId: selectedTutor.tutorId,
            market: selectedTutorMarket,
            amount: parseFloat(adjustmentForm.amount),
            note: adjustmentForm.note,
            date: adjustmentForm.date
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save adjustment');
      }

      // Clear form and refresh data
      setAdjustmentForm({ amount: '', note: '', date: '' });
      setEditingPayAdjustmentIndex(null);
      await fetchProfitShareData();

      alert(editingPayAdjustmentIndex !== null ? 'Adjustment updated successfully!' : 'Adjustment added successfully!');
    } catch (error) {
      console.error('Error saving adjustment:', error);
      alert('Failed to save adjustment: ' + error.message);
    }
  };
  
  const handleDeleteAdjustment = async (index) => {
    if (!window.confirm('Are you sure you want to delete this adjustment?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/profit-share/${selectedYear}/${selectedMonth}/tutor-adjustment/${selectedTutor.tutorId}/${selectedTutorMarket}/${index}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to delete adjustment');
      }
      
      // Refresh data
      await fetchProfitShareData();
      
      alert('Adjustment deleted successfully!');
    } catch (error) {
      console.error('Error deleting adjustment:', error);
      alert('Failed to delete adjustment: ' + error.message);
    }
  };

  const handleEditRevenueAdjustment = (index) => {
    const adjustment = selectedTutor.revenueAdjustments[index];
    setTutorRevenueForm({
      amount: adjustment.amount.toString(),
      note: adjustment.note || '',
      date: adjustment.date ? new Date(adjustment.date).toISOString().split('T')[0] : ''
    });
    setEditingRevenueAdjustmentIndex(index);
  };

  const handleAddTutorRevenueAdjustment = async () => {
    if (!tutorRevenueForm.amount || parseFloat(tutorRevenueForm.amount) === 0) {
      alert('Please enter a non-zero amount');
      return;
    }

    if (!tutorRevenueForm.date) {
      alert('Please select a date for this adjustment');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // If editing, delete old and add new
      if (editingRevenueAdjustmentIndex !== null) {
        // Delete the old adjustment
        await fetch(
          `/api/profit-share/${selectedYear}/${selectedMonth}/tutor-revenue-adjustment/${selectedTutor.tutorId}/${selectedTutorMarket}/${editingRevenueAdjustmentIndex}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
      }
      
      // Add the new/updated adjustment
      const response = await fetch(
        `/api/profit-share/${selectedYear}/${selectedMonth}/tutor-revenue-adjustment`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            tutorId: selectedTutor.tutorId,
            market: selectedTutorMarket,
            amount: parseFloat(tutorRevenueForm.amount),
            note: tutorRevenueForm.note,
            date: tutorRevenueForm.date
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save revenue adjustment');
      }

      // Clear form and refresh data
      setTutorRevenueForm({ amount: '', note: '', date: '' });
      setEditingRevenueAdjustmentIndex(null);
      await fetchProfitShareData();

      alert(editingRevenueAdjustmentIndex !== null ? 'Revenue adjustment updated successfully!' : 'Revenue adjustment added successfully!');
    } catch (error) {
      console.error('Error saving revenue adjustment:', error);
      alert('Failed to save revenue adjustment: ' + error.message);
    }
  };

  const handleDeleteTutorRevenueAdjustment = async (index) => {
    if (!window.confirm('Are you sure you want to delete this revenue adjustment?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/profit-share/${selectedYear}/${selectedMonth}/tutor-revenue-adjustment/${selectedTutor.tutorId}/${selectedTutorMarket}/${index}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete revenue adjustment');
      }

      // Refresh data
      await fetchProfitShareData();

      alert('Revenue adjustment deleted successfully!');
    } catch (error) {
      console.error('Error deleting revenue adjustment:', error);
      alert('Failed to delete revenue adjustment: ' + error.message);
    }
  };

  const handleAddSchoolRevenue = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = editingSchoolRevenueIndex !== null
        ? `/api/profit-share/${selectedYear}/${selectedMonth}/school-revenue/${editingSchoolRevenueIndex}`
        : `/api/profit-share/${selectedYear}/${selectedMonth}/school-revenue`;

      const method = editingSchoolRevenueIndex !== null ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tutorId: schoolRevenueForm.tutorId,
          tutorName: schoolRevenueForm.tutorName,
          amount: parseFloat(schoolRevenueForm.amount),
          description: schoolRevenueForm.description,
          market: schoolRevenueForm.market,
          date: new Date()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save school revenue adjustment');
      }

      await fetchProfitShareData();
      setShowSchoolRevenueModal(false);
      setEditingSchoolRevenueIndex(null);
      setSchoolRevenueForm({ tutorId: '', tutorName: '', amount: '', description: '', market: 'LA' });
    } catch (err) {
      console.error('Error saving school revenue adjustment:', err);
      alert('Failed to save school revenue adjustment');
    }
  };

  const handleDeleteSchoolRevenue = async (index) => {
    if (!window.confirm('Are you sure you want to delete this school revenue adjustment?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/profit-share/${selectedYear}/${selectedMonth}/school-revenue/${index}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete school revenue adjustment');
      }

      await fetchProfitShareData();
    } catch (err) {
      console.error('Error deleting school revenue adjustment:', err);
      alert('Failed to delete school revenue adjustment');
    }
  };

  const handleAddOfficeHours = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = editingOfficeHoursIndex !== null
        ? `/api/profit-share/${selectedYear}/${selectedMonth}/office-hours/${editingOfficeHoursIndex}`
        : `/api/profit-share/${selectedYear}/${selectedMonth}/office-hours`;
      
      const method = editingOfficeHoursIndex !== null ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tutorId: officeHoursForm.tutorId,
          tutorName: officeHoursForm.tutorName,
          hours: parseFloat(officeHoursForm.hours),
          rate: parseFloat(officeHoursForm.rate),
          description: officeHoursForm.description,
          date: new Date()
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save office hours');
      }
      
      await fetchProfitShareData();
      setShowOfficeHoursModal(false);
      setEditingOfficeHoursIndex(null);
      setOfficeHoursForm({ tutorId: '', tutorName: '', hours: '', rate: '25', description: '' });
    } catch (err) {
      console.error('Error saving office hours:', err);
      alert('Failed to save office hours');
    }
  };
  
  const handleDeleteOfficeHours = async (index) => {
    if (!window.confirm('Are you sure you want to delete this office hours entry?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/profit-share/${selectedYear}/${selectedMonth}/office-hours/${index}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to delete office hours');
      }
      
      await fetchProfitShareData();
    } catch (err) {
      console.error('Error deleting office hours:', err);
      alert('Failed to delete office hours');
    }
  };
  
  const handleAddMileage = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = editingMileageIndex !== null
        ? `/api/profit-share/${selectedYear}/${selectedMonth}/mileage/${editingMileageIndex}`
        : `/api/profit-share/${selectedYear}/${selectedMonth}/mileage`;
      
      const method = editingMileageIndex !== null ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tutorId: mileageForm.tutorId,
          tutorName: mileageForm.tutorName,
          amount: parseFloat(mileageForm.amount),
          description: mileageForm.description,
          date: new Date()
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save mileage');
      }
      
      await fetchProfitShareData();
      setShowMileageModal(false);
      setEditingMileageIndex(null);
      setMileageForm({ tutorId: '', tutorName: '', amount: '', description: '' });
    } catch (err) {
      console.error('Error saving mileage:', err);
      alert('Failed to save mileage');
    }
  };
  
  const handleDeleteMileage = async (index) => {
    if (!window.confirm('Are you sure you want to delete this mileage entry?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/profit-share/${selectedYear}/${selectedMonth}/mileage/${index}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to delete mileage');
      }
      
      await fetchProfitShareData();
    } catch (err) {
      console.error('Error deleting mileage:', err);
      alert('Failed to delete mileage');
    }
  };
  
  const handleAddSoftwareLicense = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = editingSoftwareLicenseIndex !== null
        ? `/api/profit-share/${selectedYear}/${selectedMonth}/software-license/${editingSoftwareLicenseIndex}`
        : `/api/profit-share/${selectedYear}/${selectedMonth}/software-license`;

      const method = editingSoftwareLicenseIndex !== null ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(softwareLicenseForm.amount),
          description: softwareLicenseForm.description,
          date: new Date()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save software license');
      }

      await fetchProfitShareData();
      setShowSoftwareLicenseModal(false);
      setEditingSoftwareLicenseIndex(null);
      setSoftwareLicenseForm({ amount: '', description: '' });
    } catch (err) {
      console.error('Error saving software license:', err);
    }
  };

  const handleDeleteSoftwareLicense = async (index) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/profit-share/${selectedYear}/${selectedMonth}/software-license/${index}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete software license');
      }

      await fetchProfitShareData();
    } catch (err) {
      console.error('Error deleting software license:', err);
    }
  };

  const handleUpdateSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/profit-share/${selectedYear}/${selectedMonth}/settings`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(settingsForm)
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to update settings');
      }
      
      await fetchProfitShareData();
      setShowSettingsModal(false);
    } catch (err) {
      console.error('Error updating settings:', err);
      alert('Failed to update settings');
    }
  };
  
  if (loading) {
    return (
      <div className="profit-share-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading profit share data...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="profit-share-container">
        <div className="error-state">
          <h2>⚠️ Error Loading Profit Share</h2>
          <p>{error}</p>
          <button onClick={fetchProfitShareData} className="retry-btn">
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }
  
  if (!profitShareData && !annualData) {
    return (
      <div className="profit-share-container">
        <div className="no-data-state">
          <h2>📊 No Profit Share Data Available</h2>
          <p>No data found for the selected period.</p>
        </div>
      </div>
    );
  }
  
  const summary = profitShareData?.summary || {};
  
  return (
    <div className="profit-share-container">
      {/* Header */}
      <div className="profit-share-header">
        <div className="header-content">
          <h1>💰 Profit Share Dashboard</h1>
          <p>Track revenue, tutor pay, and calculate profit share across different markets and clubs</p>
        </div>
        
        {/* Tabs */}
        <div className="profit-share-tabs">
          <button
            className={`tab-btn ${activeTab === 'california' ? 'active' : ''}`}
            onClick={() => setActiveTab('california')}
          >
            🌴 California
          </button>
          <button
            className={`tab-btn ${activeTab === 'park-slope' ? 'active' : ''}`}
            onClick={() => setActiveTab('park-slope')}
          >
            🏢 Park Slope Club
          </button>
          <button
            className={`tab-btn ${activeTab === 'ues' ? 'active' : ''}`}
            onClick={() => setActiveTab('ues')}
          >
            🏙️ Upper East Side Club
          </button>
          <button
            className={`tab-btn ${activeTab === 'travel-stipend' ? 'active' : ''}`}
            onClick={() => setActiveTab('travel-stipend')}
          >
            🚗 Travel Stipend
          </button>
        </div>
        
        <div className="date-selectors">
          <div className="selector-group">
            <label htmlFor="month-select">Month:</label>
            <select
              id="month-select"
              value={selectedMonth}
              onChange={(e) => {
                const newMonth = parseInt(e.target.value);
                setSelectedMonth(newMonth);
                saveMonthYear(selectedYear, newMonth);
              }}
              className="date-dropdown"
            >
              {months.map(month => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
          </div>
          <div className="selector-group">
            <label htmlFor="year-select">Year:</label>
            <select
              id="year-select"
              value={selectedYear}
              onChange={(e) => {
                const newYear = parseInt(e.target.value);
                setSelectedYear(newYear);
                saveMonthYear(newYear, selectedMonth);
              }}
              className="date-dropdown"
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          {activeTab === 'california' && !isAnnualView && (
            <>
              <button
                onClick={handleImportFromPreviousMonth}
                className="import-btn"
                title="Import tutors from previous month"
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginLeft: '12px'
                }}
              >
                📥 Import from Previous Month
              </button>
              <button
                onClick={() => setShowSettingsModal(true)}
                className="settings-btn"
                title="Update Settings"
              >
                ⚙️ Settings
              </button>
            </>
          )}
        </div>
      </div>
      
      {/* Main Content */}
      {activeTab === 'california' ? (
        <>
        <div className="main-content" style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '24px',
          width: '100%',
          alignItems: 'start'
        }}>
          {/* Left Column - Markets and Manual Entries */}
          <div className="left-column" style={{display: 'flex', flexDirection: 'column', gap: '24px'}}>
            {/* LA Market Section */}
            <div className="market-section">
              <div className="market-header">
                <h2>🌴 Los Angeles Market {isAnnualView && <span style={{fontSize: '0.8em', fontWeight: 'normal', color: '#666'}}>(Annual/YTD {selectedYear})</span>}</h2>
                {!isAnnualView && (
                  <button
                    onClick={() => {
                      setAddTutorMarket('LA');
                      setShowAddTutorModal(true);
                    }}
                    className="add-tutor-btn"
                  >
                    + Add Tutor
                  </button>
                )}
              </div>
            {isAnnualView && annualData ? (
              <div style={{display: 'flex', flexDirection: 'column', gap: '24px'}}>
                {/* Annual Summary - Simplified */}
                <div style={{
                  backgroundColor: '#f9fafb',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '32px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}>
                  <h3 style={{marginTop: 0, marginBottom: '24px', fontSize: '24px', fontWeight: '600', color: '#1a202c'}}>
                    📊 Annual Summary - LA Market {selectedYear}
                  </h3>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '20px',
                    marginBottom: '24px'
                  }}>
                    {/* Revenue Section */}
                    <div style={{
                      backgroundColor: 'white',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{fontSize: '14px', color: '#6b7280', marginBottom: '8px'}}>Total Revenue</div>
                      <div style={{fontSize: '32px', fontWeight: '700', color: '#10b981'}}>
                        {formatCurrency(annualData.summary.totalRevenue)}
                      </div>
                      {annualData.summary.schoolRevenue > 0 && (
                        <div style={{fontSize: '12px', color: '#6b7280', marginTop: '4px'}}>
                          (Appointment: {formatCurrency(annualData.summary.appointmentRevenue)} + School: {formatCurrency(annualData.summary.schoolRevenue)})
                        </div>
                      )}
                    </div>

                    {/* Expenses Section */}
                    <div style={{
                      backgroundColor: 'white',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{fontSize: '14px', color: '#6b7280', marginBottom: '8px'}}>Total Expenses</div>
                      <div style={{fontSize: '32px', fontWeight: '700', color: '#ef4444'}}>
                        {formatCurrency(
                          annualData.summary.totalTutorPay +
                          annualData.summary.officeHours +
                          annualData.summary.totalAdSpend +
                          annualData.summary.totalMileage +
                          (annualData.summary.stripeProcessingFee || 0) +
                          (annualData.summary.tcProcessingFee || 0)
                        )}
                      </div>
                      <div style={{fontSize: '12px', color: '#6b7280', marginTop: '4px'}}>
                        Tutor Pay: {formatCurrency(annualData.summary.totalTutorPay)}<br/>
                        Office Hours: {formatCurrency(annualData.summary.officeHours)}<br/>
                        Ad Spend: {formatCurrency(annualData.summary.totalAdSpend)}<br/>
                        Mileage: {formatCurrency(annualData.summary.totalMileage)}
                        {(annualData.summary.stripeProcessingFee || 0) > 0 && (<><br/>Stripe Processing (2.9%): {formatCurrency(annualData.summary.stripeProcessingFee)}</>)}
                        {(annualData.summary.tcProcessingFee || 0) > 0 && (<><br/>TutorCruncher Processing (2%): {formatCurrency(annualData.summary.tcProcessingFee)}</>)}
                      </div>
                    </div>

                    {/* Profit Section */}
                    <div style={{
                      backgroundColor: 'white',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{fontSize: '14px', color: '#6b7280', marginBottom: '8px'}}>Gross Profit</div>
                      <div style={{fontSize: '32px', fontWeight: '700', color: annualData.summary.grossProfit >= 0 ? '#10b981' : '#ef4444'}}>
                        {formatCurrency(annualData.summary.grossProfit)}
                      </div>
                    </div>

                    {/* Net Profit Section */}
                    <div style={{
                      backgroundColor: 'white',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{fontSize: '14px', color: '#6b7280', marginBottom: '8px'}}>Net Profit</div>
                      <div style={{fontSize: '32px', fontWeight: '700', color: annualData.summary.netProfit >= 0 ? '#10b981' : '#ef4444'}}>
                        {formatCurrency(annualData.summary.netProfit)}
                      </div>
                      <div style={{fontSize: '12px', color: '#6b7280', marginTop: '4px'}}>
                        (After Caitlin Salary: {formatCurrency(annualData.summary.caitlinSalary)})
                      </div>
                    </div>
                  </div>

                  {/* Profit Share Section */}
                  <div style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    padding: '24px',
                    borderRadius: '8px',
                    marginTop: '20px',
                    textAlign: 'center'
                  }}>
                    <div style={{fontSize: '16px', marginBottom: '8px', opacity: 0.9}}>
                      Profit Share ({annualData.summary.profitSharePercentage}%)
                    </div>
                    <div style={{fontSize: '48px', fontWeight: '700', marginBottom: '8px'}}>
                      {formatCurrency(annualData.summary.profitShareAmount)}
                    </div>
                    <div style={{fontSize: '20px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.3)'}}>
                      <strong>Caitlin Total Pay:</strong> {formatCurrency(annualData.summary.caitlinTotalPay)}
                    </div>
                  </div>

                  {annualData.months && annualData.months.length > 0 && (
                    <div style={{
                      marginTop: '20px',
                      padding: '12px',
                      backgroundColor: '#f3f4f6',
                      borderRadius: '8px',
                      fontSize: '14px',
                      color: '#666',
                      textAlign: 'center'
                    }}>
                      <strong>Months included:</strong> {annualData.months.map(m => m.monthName).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            ) : profitShareData && profitShareData.laMarket ? (
              <>
                <div className="market-totals">
                  <div className="total-item">
                    <span>Appointment Revenue:</span>
                    <span className="total-value">{formatCurrency(profitShareData.laMarket.appointmentRevenue)}</span>
                  </div>
                  {(() => {
                    const laSchoolRevenue = config?.schoolRevenueAdjustments?.filter(adj => adj.market === 'LA').reduce((sum, adj) => sum + (adj.amount || 0), 0) || 0;
                    return laSchoolRevenue > 0 && (
                      <div className="total-item">
                        <span>School Revenue:</span>
                        <span className="total-value" style={{color: '#10b981'}}>{formatCurrency(laSchoolRevenue)}</span>
                      </div>
                    );
                  })()}
                  <div className="total-item" style={{borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '8px'}}>
                    <span><strong>Total Revenue:</strong></span>
                    <span className="total-value"><strong>{formatCurrency(profitShareData.laMarket.totalRevenue)}</strong></span>
                  </div>
                  <div className="total-item">
                    <span>Total Tutor Pay:</span>
                    <span className="total-value">{formatCurrency(profitShareData.laMarket.totalTutorPay)}</span>
                  </div>
                </div>
                <div className="tutors-table">
                  {profitShareData.laMarket.tutors.length === 0 ? (
                    <div className="empty-state">
                      <p>No tutors added to LA market yet</p>
                    </div>
                  ) : (
                    <table>
                      <thead>
                        <tr>
                          <th>Tutor Name</th>
                          <th>Lessons</th>
                          <th>Revenue</th>
                          <th>Tutor Pay</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profitShareData.laMarket.tutors.map((tutor) => (
                          <tr key={tutor.tutorId}>
                            <td>
                              <button
                                className="tutor-name-btn"
                                onClick={() => handleViewTutorLessons(tutor, 'LA')}
                              >
                                {tutor.tutorName}
                              </button>
                            </td>
                            <td>{tutor.lessonCount}</td>
                            <td>{formatCurrency(tutor.totalRevenue)}</td>
                            <td>{formatCurrency(tutor.totalTutorPay)}</td>
                            <td>
                              <button
                                onClick={() => handleRemoveTutor(tutor.tutorId, 'LA')}
                                className="remove-btn"
                                title="Remove tutor"
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            ) : (
              <div className="empty-state">
                <p>Loading...</p>
              </div>
            )}
          </div>
          
          {/* SF Market Section - Hidden in Annual View */}
          {!isAnnualView && (
          <div className="market-section">
            <div className="market-header">
              <h2>🌉 San Francisco Market</h2>
              <button
                onClick={() => {
                  setAddTutorMarket('SF');
                  setShowAddTutorModal(true);
                }}
                className="add-tutor-btn"
              >
                + Add Tutor
              </button>
            </div>
            <div className="market-totals">
              <div className="total-item">
                <span>Appointment Revenue:</span>
                <span className="total-value">{formatCurrency(profitShareData?.sfMarket?.appointmentRevenue || 0)}</span>
              </div>
              {(() => {
                const sfSchoolRevenue = config?.schoolRevenueAdjustments?.filter(adj => adj.market === 'SF').reduce((sum, adj) => sum + (adj.amount || 0), 0) || 0;
                return sfSchoolRevenue > 0 && (
                  <div className="total-item">
                    <span>School Revenue:</span>
                    <span className="total-value" style={{color: '#10b981'}}>{formatCurrency(sfSchoolRevenue)}</span>
                  </div>
                );
              })()}
              <div className="total-item" style={{borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '8px'}}>
                <span><strong>Total Revenue:</strong></span>
                <span className="total-value"><strong>{formatCurrency(profitShareData?.sfMarket?.totalRevenue || 0)}</strong></span>
              </div>
              <div className="total-item">
                <span>Total Tutor Pay:</span>
                <span className="total-value">{formatCurrency(profitShareData?.sfMarket?.totalTutorPay || 0)}</span>
              </div>
            </div>
            <div className="tutors-table">
              {(!profitShareData?.sfMarket || !profitShareData.sfMarket.tutors || profitShareData.sfMarket.tutors.length === 0) ? (
                <div className="empty-state">
                  <p>No tutors added to SF market yet</p>
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Tutor Name</th>
                      <th>Lessons</th>
                      <th>Revenue</th>
                      <th>Tutor Pay</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profitShareData.sfMarket.tutors.map((tutor) => (
                      <tr key={tutor.tutorId}>
                        <td>
                          <button
                            className="tutor-name-btn"
                            onClick={() => handleViewTutorLessons(tutor, 'SF')}
                          >
                            {tutor.tutorName}
                          </button>
                        </td>
                        <td>{tutor.lessonCount}</td>
                        <td>{formatCurrency(tutor.totalRevenue)}</td>
                        <td>{formatCurrency(tutor.totalTutorPay)}</td>
                        <td>
                          <button
                            onClick={() => handleRemoveTutor(tutor.tutorId, 'SF')}
                            className="remove-btn"
                            title="Remove tutor"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          )}
        </div>
        
        {/* Right Column - Summary and Manual Entries */}
        {!isAnnualView && (
        <div className="right-column">
          {/* Summary Card */}
          <div className="summary-card">
            <h2>📊 Profit Share Summary</h2>
            <div className="summary-grid">
              <div className="summary-row">
                <span className="summary-label">LA Revenue</span>
                <span className="summary-value">{formatCurrency(summary.laRevenue)}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">SF Revenue</span>
                <span className="summary-value">{formatCurrency(summary.sfRevenue)}</span>
              </div>
              <div className="summary-row total-row">
                <span className="summary-label">Total Revenue</span>
                <span className="summary-value">{formatCurrency(summary.totalRevenue)}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Tutor Pay</span>
                <span className="summary-value negative">{formatCurrency(summary.totalTutorPay + summary.officeHours)}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Ad Spend</span>
                <span className="summary-value negative">{formatCurrency(summary.totalAdSpend)}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Mileage</span>
                <span className="summary-value negative">{formatCurrency(summary.laMileage)}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Software Licenses</span>
                <span className="summary-value negative">{formatCurrency(summary.totalSoftwareLicenses)}</span>
              </div>
              {summary.stripeProcessingFee > 0 && (
                <div className="summary-row">
                  <span className="summary-label">Stripe Processing (2.9%)</span>
                  <span className="summary-value negative">{formatCurrency(summary.stripeProcessingFee)}</span>
                </div>
              )}
              {summary.tcProcessingFee > 0 && (
                <div className="summary-row">
                  <span className="summary-label">TutorCruncher Processing (2%)</span>
                  <span className="summary-value negative">{formatCurrency(summary.tcProcessingFee)}</span>
                </div>
              )}
              <div className="summary-row highlight-row">
                <span className="summary-label">Gross Profit</span>
                <span className="summary-value">{formatCurrency(summary.grossProfit)}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Caitlin Salary</span>
                <span className="summary-value negative">{formatCurrency(summary.caitlinSalary)}</span>
              </div>
              <div className="summary-row highlight-row">
                <span className="summary-label">Net Profit</span>
                <span className="summary-value">{formatCurrency(summary.netProfit)}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Profit Share ({summary.profitSharePercentage}%)</span>
                <span className="summary-value profit-share">{formatCurrency(summary.profitShareAmount)}</span>
              </div>
              <div className="summary-row total-row final-row">
                <span className="summary-label">Caitlin Total Pay</span>
                <span className="summary-value profit-share">{formatCurrency(summary.caitlinTotalPay)}</span>
              </div>
            </div>
          </div>

          {/* Office Hours & Non-Teaching Work Section */}
          <div className="manual-section" style={{marginTop: '24px'}}>
            <div className="manual-header">
              <h2>⏰ Office Hours & Non-Teaching Work</h2>
              <button
                onClick={() => {
                  setShowOfficeHoursModal(true);
                  setEditingOfficeHoursIndex(null);
                  setOfficeHoursForm({ tutorId: '', tutorName: '', hours: '', rate: '25', description: '' });
                }}
                className="add-btn"
              >
                + Add Entry
              </button>
            </div>
            {config?.officeHours?.length === 0 ? (
              <div className="empty-state">
                <p>No office hours entries yet</p>
              </div>
            ) : (
              <div className="manual-entries">
                {config?.officeHours?.map((entry, index) => (
                  <div key={index} className="manual-entry">
                    <div className="entry-info">
                      <div className="entry-tutor">{entry.tutorName || 'Unknown Tutor'}</div>
                      <div className="entry-details">
                        {entry.hours} hours × {formatCurrency(entry.rate)}/hr
                        {entry.description && <span className="entry-desc"> - {entry.description}</span>}
                      </div>
                    </div>
                    <div className="entry-actions">
                      <span className="entry-amount">{formatCurrency(entry.amount)}</span>
                      <button
                        onClick={() => {
                          setEditingOfficeHoursIndex(index);
                          setOfficeHoursForm({
                            tutorId: entry.tutorId || '',
                            tutorName: entry.tutorName || '',
                            hours: entry.hours?.toString() || '',
                            rate: entry.rate?.toString() || '25',
                            description: entry.description || ''
                          });
                          setShowOfficeHoursModal(true);
                        }}
                        className="edit-btn"
                        title="Edit"
                      >
                        ✎
                      </button>
                      <button
                        onClick={() => handleDeleteOfficeHours(index)}
                        className="delete-btn"
                        title="Delete"
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                ))}
                <div className="entries-total">
                  <span>Total Office Hours Pay:</span>
                  <span className="total-value">{formatCurrency(summary.officeHours)}</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Mileage Reimbursement Section */}
          <div className="manual-section" style={{marginTop: '24px'}}>
            <div className="manual-header">
              <h2>🚗 Mileage Reimbursement</h2>
              <button
                onClick={() => {
                  setShowMileageModal(true);
                  setEditingMileageIndex(null);
                  setMileageForm({ tutorId: '', tutorName: '', miles: '', rate: '0.67', description: '' });
                }}
                className="add-btn"
              >
                + Add Entry
              </button>
            </div>
            {config?.mileageReimbursements?.length === 0 ? (
              <div className="empty-state">
                <p>No mileage entries yet</p>
              </div>
            ) : (
              <div className="manual-entries">
                {config?.mileageReimbursements?.map((entry, index) => (
                  <div key={index} className="manual-entry">
                    <div className="entry-info">
                      <div className="entry-tutor">{entry.tutorName || 'Unknown Tutor'}</div>
                      <div className="entry-details">
                        {entry.miles} miles × {formatCurrency(entry.rate)}/mile
                        {entry.description && <span className="entry-desc"> - {entry.description}</span>}
                      </div>
                    </div>
                    <div className="entry-actions">
                      <span className="entry-amount">{formatCurrency(entry.amount)}</span>
                      <button
                        onClick={() => {
                          setEditingMileageIndex(index);
                          setMileageForm({
                            tutorId: entry.tutorId || '',
                            tutorName: entry.tutorName || '',
                            amount: entry.amount?.toString() || '',
                            description: entry.description || ''
                          });
                          setShowMileageModal(true);
                        }}
                        className="edit-btn"
                        title="Edit"
                      >
                        ✎
                      </button>
                      <button
                        onClick={() => handleDeleteMileage(index)}
                        className="delete-btn"
                        title="Delete"
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                ))}
                <div className="entries-total">
                  <span>Total Mileage Reimbursement:</span>
                  <span className="total-value">{formatCurrency(summary.totalMileage)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Software Licenses Section */}
          <div className="manual-section" style={{marginTop: '24px'}}>
            <div className="manual-header">
              <h2>💻 Monthly Software Licenses</h2>
              <button
                onClick={() => {
                  setShowSoftwareLicenseModal(true);
                  setEditingSoftwareLicenseIndex(null);
                  setSoftwareLicenseForm({ amount: '', description: '' });
                }}
                className="add-btn"
              >
                + Add Entry
              </button>
            </div>
            <p style={{fontSize: '12px', color: '#6b7280', margin: '0 0 12px 0', lineHeight: '1.4'}}>
              Monthly software expenses per user (e.g., Zoom, Slack, G Suite). Add a line item per user with details in the description.
            </p>
            {(config?.softwareLicenses || []).length === 0 ? (
              <div className="empty-state">
                <p>No software license entries yet</p>
              </div>
            ) : (
              <div className="manual-entries">
                {(config?.softwareLicenses || []).map((entry, index) => (
                  <div key={index} className="manual-entry">
                    <div className="entry-info">
                      <div className="entry-details">
                        {entry.description || 'Software License'}
                      </div>
                    </div>
                    <div className="entry-actions">
                      <span className="entry-amount">{formatCurrency(entry.amount)}</span>
                      <button
                        onClick={() => {
                          setEditingSoftwareLicenseIndex(index);
                          setSoftwareLicenseForm({
                            amount: entry.amount?.toString() || '',
                            description: entry.description || ''
                          });
                          setShowSoftwareLicenseModal(true);
                        }}
                        className="edit-btn"
                        title="Edit"
                      >
                        ✎
                      </button>
                      <button
                        onClick={() => handleDeleteSoftwareLicense(index)}
                        className="delete-btn"
                        title="Delete"
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                ))}
                <div className="entries-total">
                  <span>Total Software Licenses:</span>
                  <span className="total-value">{formatCurrency(summary.totalSoftwareLicenses)}</span>
                </div>
              </div>
            )}
          </div>

          {/* School Revenue Adjustments Section */}
          <div className="section">
            <div className="section-header">
              <h2>🎓 School Revenue Adjustments</h2>
              <button
                onClick={() => setShowSchoolRevenueModal(true)}
                className="add-entry-btn"
              >
                + Add Entry
              </button>
            </div>

            {config?.schoolRevenueAdjustments && config.schoolRevenueAdjustments.length > 0 ? (
              <div className="entries-list">
                {config.schoolRevenueAdjustments.map((entry, index) => (
                  <div key={index} className="entry-item">
                    <div className="entry-info">
                      <div className="entry-main">
                        <span className="entry-tutor">{entry.tutorName}</span>
                        <span className={`entry-market-badge ${entry.market.toLowerCase()}`}>
                          {entry.market}
                        </span>
                        {entry.description && <span className="entry-desc"> - {entry.description}</span>}
                      </div>
                    </div>
                    <div className="entry-actions">
                      <span className="entry-amount">{formatCurrency(entry.amount)}</span>
                      <button
                        onClick={() => {
                          setEditingSchoolRevenueIndex(index);
                          setSchoolRevenueForm({
                            tutorId: entry.tutorId || '',
                            tutorName: entry.tutorName || '',
                            amount: entry.amount?.toString() || '',
                            description: entry.description || '',
                            market: entry.market || 'LA'
                          });
                          setShowSchoolRevenueModal(true);
                        }}
                        className="edit-btn"
                        title="Edit"
                      >
                        ✎
                      </button>
                      <button
                        onClick={() => handleDeleteSchoolRevenue(index)}
                        className="delete-btn"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
                <div className="entries-total">
                  <span>Total School Revenue Adjustments:</span>
                  <span className="total-value">
                    {formatCurrency(
                      config.schoolRevenueAdjustments.reduce((sum, adj) => sum + (adj.amount || 0), 0)
                    )}
                  </span>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <p>No school revenue adjustments added yet</p>
              </div>
            )}
          </div>
        </div>
        )}
        </div>
      </>
      ) : (activeTab === 'park-slope' || activeTab === 'ues') ? (
        /* Club Content */
        <div className="club-content">
          {clubLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading club data...</p>
            </div>
          ) : clubData[activeTab] ? (
            <div className="main-content" style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              gap: '24px',
              width: '100%',
              alignItems: 'start'
            }}>
              {/* Left Column - Club Breakdown */}
              <div className="left-column" style={{display: 'flex', flexDirection: 'column', gap: '24px'}}>
                {/* Lessons Section */}
                <div className="club-section">
                  <div className="club-section-header">
                    <h2>📚 Club Lessons</h2>
                  </div>
                  <div className="club-totals">
                    <div className="total-item">
                      <span>Total Lessons:</span>
                      <span className="total-value">{clubData[activeTab].metrics.lessonCount}</span>
                    </div>
                    <div className="total-item">
                      <span>Revenue:</span>
                      <span className="total-value">{formatCurrency(clubData[activeTab].metrics.totalRevenue)}</span>
                    </div>
                    <div className="total-item">
                      <span>Tutor Pay:</span>
                      <span className="total-value">{formatCurrency(clubData[activeTab].metrics.totalTutorPay)}</span>
                    </div>
                    <div className="total-item" style={{borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '8px'}}>
                      <span><strong>Net Profit:</strong></span>
                      <span className="total-value"><strong>{formatCurrency(clubData[activeTab].metrics.totalRevenue - clubData[activeTab].metrics.totalTutorPay)}</strong></span>
                    </div>
                    <button
                      onClick={() => handleViewClubLessons(clubData[activeTab].metrics.lessons, 'lessons', clubData[activeTab].club)}
                      className="view-details-btn"
                      style={{
                        marginTop: '12px',
                        padding: '8px 16px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      View All Lessons ({clubData[activeTab].metrics.lessonCount})
                    </button>
                  </div>
                </div>

                {/* Support Section */}
                <div className="club-section">
                  <div className="club-section-header">
                    <h2>🔧 Club Support</h2>
                  </div>
                  <div className="club-totals">
                    <div className="total-item">
                      <span>Support Hours:</span>
                      <span className="total-value">{clubData[activeTab].metrics.supportCount}</span>
                    </div>
                    <div className="total-item">
                      <span>Revenue:</span>
                      <span className="total-value">{formatCurrency(clubData[activeTab].metrics.supportRevenue)}</span>
                    </div>
                    <div className="total-item">
                      <span>Tutor Pay:</span>
                      <span className="total-value">{formatCurrency(clubData[activeTab].metrics.supportTutorPay)}</span>
                    </div>
                    <div className="total-item" style={{borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '8px'}}>
                      <span><strong>Net Profit:</strong></span>
                      <span className="total-value"><strong>{formatCurrency(clubData[activeTab].metrics.supportRevenue - clubData[activeTab].metrics.supportTutorPay)}</strong></span>
                    </div>
                    <button
                      onClick={() => handleViewClubLessons(clubData[activeTab].metrics.supportAppointments, 'support', clubData[activeTab].club)}
                      className="view-details-btn"
                      style={{
                        marginTop: '12px',
                        padding: '8px 16px',
                        backgroundColor: '#8b5cf6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      View All Support ({clubData[activeTab].metrics.supportCount})
                    </button>
                  </div>
                </div>
              </div>

              {/* Right Column - Profit Share Summary */}
              <div className="right-column">
                <div className="summary-card">
                  <h2>📊 {clubData[activeTab].club} Profit Share</h2>
                  <div className="summary-grid">
                    <div className="summary-row">
                      <span className="summary-label">Total Revenue</span>
                      <span className="summary-value">{formatCurrency(clubData[activeTab].summary.totalRevenue)}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Tutor Pay</span>
                      <span className="summary-value negative">{formatCurrency(clubData[activeTab].summary.totalTutorPay)}</span>
                    </div>
                    <div className="summary-row highlight-row">
                      <span className="summary-label">Net Profit</span>
                      <span className="summary-value">{formatCurrency(clubData[activeTab].summary.netProfit)}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Profit Share ({clubData[activeTab].summary.profitSharePercentage}%)</span>
                      <span className="summary-value profit-share">{formatCurrency(clubData[activeTab].summary.profitShareAmount)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => downloadCombinedClubCSV(clubData[activeTab], clubData[activeTab].club)}
                    style={{
                      marginTop: '16px',
                      padding: '10px 20px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: '100%',
                      justifyContent: 'center'
                    }}
                  >
                    📥 Download Combined CSV
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-data-state">
              <h2>📊 No Club Data Available</h2>
              <p>No data found for {activeTab === 'park-slope' ? 'Park Slope Club' : 'Upper East Side Club'} for the selected period.</p>
            </div>
          )}
        </div>
      ) : activeTab === 'travel-stipend' ? (
        /* Travel Stipend Content */
        <div className="club-content">
          {travelStipendLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading travel stipend data...</p>
            </div>
          ) : travelStipendData ? (
            <div className="main-content" style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              width: '100%'
            }}>
              {/* Travel Stipend Header */}
              <div className="club-section">
                <div className="club-section-header">
                  <h2>🚗 Tutor Travel Stipend</h2>
                  <p>Weekly hours and stipend calculations for eligible tutors</p>
                </div>
                <div className="club-totals">
                  <div className="total-item">
                    <span>Total Monthly Stipend:</span>
                    <span className="total-value">{formatCurrency(travelStipendData.totalMonthlyStipend)}</span>
                  </div>
                  <div className="total-item">
                    <span>Number of Weeks:</span>
                    <span className="total-value">{travelStipendData.weeks.length}</span>
                  </div>
                  <div className="total-item">
                    <span>Eligible Tutors:</span>
                    <span className="total-value">{travelStipendData.travelStipendData.length}</span>
                  </div>
                </div>
              </div>

              {/* Individual Tutor Breakdowns */}
              <div className="club-section">
                <div className="club-section-header">
                  <h2>📊 Weekly Breakdown</h2>
                </div>
                
                {/* Individual Tutor Cards */}
                {travelStipendData.travelStipendData.map((tutor, tutorIndex) => (
                  <div key={tutorIndex} style={{
                    marginBottom: '24px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}>
                    {/* Tutor Header */}
                    <div style={{
                      backgroundColor: '#f8fafc',
                      padding: '16px 20px',
                      borderBottom: '1px solid #e2e8f0'
                    }}>
                      <h3 style={{
                        margin: 0,
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#1f2937'
                      }}>
                        {tutor.tutorName}
                      </h3>
                    </div>
                    
                    {/* Tutor Weekly Table */}
                    <div className="tutors-table">
                      <table style={{margin: 0}}>
                        <thead>
                          <tr>
                            <th style={{
                              backgroundColor: '#f1f5f9',
                              borderBottom: '2px solid #e2e8f0',
                              padding: '12px 16px',
                              textAlign: 'left',
                              fontWeight: '600',
                              fontSize: '14px'
                            }}>Tutor</th>
                            {travelStipendData.weeks.map((week, weekIndex) => (
                              <th key={weekIndex} style={{
                                backgroundColor: '#f1f5f9',
                                borderBottom: '2px solid #e2e8f0',
                                padding: '12px 16px',
                                textAlign: 'center',
                                fontWeight: '600',
                                fontSize: '14px'
                              }}>
                                {new Date(week.weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(week.weekEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </th>
                            ))}
                            <th style={{
                              backgroundColor: '#f1f5f9',
                              borderBottom: '2px solid #e2e8f0',
                              padding: '12px 16px',
                              textAlign: 'center',
                              fontWeight: '600',
                              fontSize: '14px'
                            }}>Monthly Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td style={{
                              padding: '16px',
                              fontWeight: '600',
                              fontSize: '16px',
                              color: '#1f2937',
                              borderBottom: '1px solid #e2e8f0'
                            }}>
                              {tutor.tutorName}
                            </td>
                            {tutor.weeks.map((week, weekIndex) => (
                              <td key={weekIndex} style={{
                                padding: '16px',
                                textAlign: 'center',
                                borderBottom: '1px solid #e2e8f0'
                              }}>
                                <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                                  <span 
                                    style={{
                                      cursor: 'pointer',
                                      color: '#3b82f6',
                                      textDecoration: 'underline',
                                      fontWeight: '600',
                                      fontSize: '16px'
                                    }}
                                    onClick={() => handleViewTravelStipendLessons(tutor, week)}
                                    title="Click to view lesson details"
                                  >
                                    {week.hours}h
                                  </span>
                                  <span style={{
                                    fontSize: '14px',
                                    color: week.stipend > 0 ? '#10B981' : '#6b7280',
                                    fontWeight: '500'
                                  }}>
                                    {formatCurrency(week.stipend)}
                                  </span>
                                </div>
                              </td>
                            ))}
                            <td style={{
                              padding: '16px',
                              textAlign: 'center',
                              fontWeight: '600',
                              fontSize: '16px',
                              color: '#1f2937',
                              borderBottom: '1px solid #e2e8f0'
                            }}>
                              {formatCurrency(tutor.monthlyTotal)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>

              {/* Stipend Rules */}
              <div className="club-section">
                <div className="club-section-header">
                  <h2>📋 Stipend Rules</h2>
                </div>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px'}}>
                  <div style={{padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                    <strong>5+ hours/week:</strong> $25
                  </div>
                  <div style={{padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                    <strong>10+ hours/week:</strong> $50
                  </div>
                  <div style={{padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                    <strong>15+ hours/week:</strong> $75
                  </div>
                  <div style={{padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                    <strong>20+ hours/week:</strong> $100
                  </div>
                </div>
                <div style={{marginTop: '12px', padding: '12px', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #f59e0b'}}>
                  <strong>Note:</strong> Weeks run Sunday-Saturday. Only weeks with at least 4 days in the month are counted. 
                  Non-teaching work is excluded from calculations.
                </div>
              </div>
            </div>
          ) : (
            <div className="no-data-state">
              <h2>📊 No Travel Stipend Data Available</h2>
              <p>No travel stipend data found for the selected period.</p>
            </div>
          )}
        </div>
      ) : null}

      {/* Tutor Lessons Modal */}
      {showTutorModal && selectedTutor && (
        <div className="modal-overlay" onClick={() => setShowTutorModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📚 Lessons for {selectedTutor.tutorName}</h2>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  onClick={() => downloadTutorLessonsCSV(selectedTutor)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
                >
                  📥 Download CSV
                </button>
                <button onClick={() => setShowTutorModal(false)} className="modal-close">✕</button>
              </div>
            </div>
            <div className="modal-body">
              <div className="lessons-summary">
                <div className="summary-item">
                  <span>Total Lessons:</span>
                  <span>{selectedTutor.lessonCount}</span>
                </div>
                <div className="summary-item">
                  <span>Total Revenue:</span>
                  <span>{formatCurrency(selectedTutor.totalRevenue)}</span>
                </div>
                <div className="summary-item">
                  <span>Total Tutor Pay:</span>
                  <span>{formatCurrency(selectedTutor.totalTutorPay)}</span>
                </div>
                {selectedTutor.adHocTotal > 0 && (
                  <div className="summary-item">
                    <span>Ad Hoc Charges:</span>
                    <span>{formatCurrency(selectedTutor.adHocTotal)}</span>
                  </div>
                )}
              </div>
              
              <div className="lessons-list">
                <h3 style={{ marginBottom: '15px', color: '#1e293b', fontSize: '16px' }}>Lessons</h3>
                {selectedTutor.appointments?.map((apt, index) => (
                  <div key={index} className="lesson-item">
                    <div className="lesson-header">
                      <span className="lesson-date">
                        {new Date(apt.start).toLocaleDateString()} {new Date(apt.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {apt.tutorsOnLesson > 1 && (
                        <span className="multi-tutor-badge">{apt.tutorsOnLesson} Tutors</span>
                      )}
                    </div>
                    <div className="lesson-details">
                      {apt.topic && (
                        <div><strong>Appointment:</strong> {apt.topic}</div>
                      )}
                      {apt.appointmentId && (
                        <div>
                          <strong>Lesson ID:</strong>{' '}
                          <a 
                            href={`https://account.storytimechess.com/cal/appointments/${apt.appointmentId}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#3b82f6',
                              textDecoration: 'none',
                              fontWeight: '500'
                            }}
                            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                          >
                            {apt.appointmentId}
                          </a>
                        </div>
                      )}
                      {apt.service_name && (
                        <div><strong>Service:</strong> {apt.service_name}</div>
                      )}
                      <div><strong>Revenue:</strong> {formatCurrency(apt.revenue)}</div>
                      <div><strong>Tutor Pay:</strong> {formatCurrency(apt.tutorPay)}</div>
                    </div>
                  </div>
                ))}
              </div>
              
              {selectedTutor.adHocCharges && selectedTutor.adHocCharges.length > 0 && (
                <div className="lessons-list" style={{ marginTop: '30px' }}>
                  <h3 style={{ marginBottom: '15px', color: '#1e293b', fontSize: '16px' }}>Ad Hoc Charges</h3>
                  {selectedTutor.adHocCharges.map((charge, index) => (
                    <div key={index} className="lesson-item">
                      <div className="lesson-header">
                        <span className="lesson-date">
                          {new Date(charge.date_occurred).toLocaleDateString()}
                        </span>
                        {charge.category_name && (
                          <span className="multi-tutor-badge" style={{ backgroundColor: '#8b5cf6' }}>
                            {charge.category_name}
                          </span>
                        )}
                      </div>
                      <div className="lesson-details">
                        <div><strong>Description:</strong> {charge.description}</div>
                        <div><strong>Amount:</strong> {formatCurrency(charge.amount)}</div>
                        {charge.chargeId && (
                          <div>
                            <strong>Charge ID:</strong>{' '}
                            <a 
                              href={`https://account.storytimechess.com/accounting/adhoccharges/${charge.chargeId}/`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#8b5cf6',
                                textDecoration: 'none',
                                fontWeight: '500'
                              }}
                              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                            >
                              {charge.chargeId}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Manual Adjustments Section */}
              <div style={{
                marginTop: '30px',
                padding: '20px',
                backgroundColor: '#fef3c7',
                borderRadius: '8px',
                border: '2px solid #f59e0b'
              }}>
                <h3 style={{ marginBottom: '15px', color: '#1e293b', fontSize: '16px', fontWeight: '600' }}>
                  ✏️ Manual Tutor Pay Adjustments
                </h3>
                
                {/* List of existing adjustments */}
                {selectedTutor.payAdjustments && selectedTutor.payAdjustments.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    {selectedTutor.payAdjustments.map((adj, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px',
                        marginBottom: '8px',
                        backgroundColor: 'white',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                            {formatCurrency(adj.amount)}
                            {adj.date && (
                              <span style={{ marginLeft: '10px', color: '#6b7280', fontSize: '13px' }}>
                                {new Date(adj.date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          {adj.note && (
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>
                              {adj.note}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleEditPayAdjustment(index)}
                            style={{
                              padding: '4px 12px',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '13px',
                              cursor: 'pointer'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteAdjustment(index)}
                            style={{
                              padding: '4px 12px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '13px',
                              cursor: 'pointer'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#dc2626'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#ef4444'}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                    <div style={{
                      marginTop: '10px',
                      padding: '10px',
                      backgroundColor: '#f0fdf4',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      <strong>Total Adjustments:</strong> {formatCurrency(selectedTutor.totalPayAdjustment || 0)}
                    </div>
                  </div>
                )}
                
                {/* Add new adjustment form */}
                <div style={{
                  paddingTop: selectedTutor.payAdjustments && selectedTutor.payAdjustments.length > 0 ? '20px' : '0',
                  borderTop: selectedTutor.payAdjustments && selectedTutor.payAdjustments.length > 0 ? '2px solid #fbbf24' : 'none'
                }}>
                  <h4 style={{ marginBottom: '12px', color: '#1e293b', fontSize: '14px', fontWeight: '600' }}>
                    Add New Adjustment
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '150px 180px 1fr 180px', gap: '10px', alignItems: 'start' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '5px' }}>
                        Amount ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={adjustmentForm.amount}
                        onChange={(e) => setAdjustmentForm({ ...adjustmentForm, amount: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                        placeholder="0.00"
                      />
                      <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '11px' }}>
                        Use negative to subtract
                      </small>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '5px' }}>
                        Date
                      </label>
                      <input
                        type="date"
                        value={adjustmentForm.date}
                        onChange={(e) => setAdjustmentForm({ ...adjustmentForm, date: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '5px' }}>
                        Note (optional)
                      </label>
                      <input
                        type="text"
                        value={adjustmentForm.note}
                        onChange={(e) => setAdjustmentForm({ ...adjustmentForm, note: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                        placeholder="e.g., Exclude bonus not in CA profit share"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '5px', opacity: 0 }}>
                        Action
                      </label>
                      <button
                        onClick={handleAddAdjustment}
                        style={{
                          width: '100%',
                          padding: '8px 20px',
                          backgroundColor: '#f59e0b',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#d97706'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#f59e0b'}
                      >
                        {editingPayAdjustmentIndex !== null ? 'Update Adjustment' : 'Add Adjustment'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Manual Revenue Adjustments Section */}
              <div style={{
                marginTop: '30px',
                padding: '20px',
                backgroundColor: '#dcfce7',
                borderRadius: '8px',
                border: '2px solid #10b981'
              }}>
                <h3 style={{ marginBottom: '15px', color: '#1e293b', fontSize: '16px', fontWeight: '600' }}>
                  💰 Manual Revenue Adjustments
                </h3>

                {/* List of existing revenue adjustments */}
                {selectedTutor.revenueAdjustments && selectedTutor.revenueAdjustments.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    {selectedTutor.revenueAdjustments.map((adj, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px',
                        marginBottom: '8px',
                        backgroundColor: 'white',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                            {formatCurrency(adj.amount)}
                            {adj.date && (
                              <span style={{ marginLeft: '10px', color: '#6b7280', fontSize: '13px' }}>
                                {new Date(adj.date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          {adj.note && (
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>
                              {adj.note}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleEditRevenueAdjustment(index)}
                            style={{
                              padding: '4px 12px',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '13px',
                              cursor: 'pointer'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteTutorRevenueAdjustment(index)}
                            style={{
                              padding: '4px 12px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '13px',
                              cursor: 'pointer'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#dc2626'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#ef4444'}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                    <div style={{
                      marginTop: '10px',
                      padding: '10px',
                      backgroundColor: '#f0fdf4',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      <strong>Total Revenue Adjustments:</strong> {formatCurrency(selectedTutor.totalRevenueAdjustment || 0)}
                    </div>
                  </div>
                )}

                {/* Add new revenue adjustment form */}
                <div style={{
                  paddingTop: selectedTutor.revenueAdjustments && selectedTutor.revenueAdjustments.length > 0 ? '20px' : '0',
                  borderTop: selectedTutor.revenueAdjustments && selectedTutor.revenueAdjustments.length > 0 ? '2px solid #10b981' : 'none'
                }}>
                  <h4 style={{ marginBottom: '12px', color: '#1e293b', fontSize: '14px', fontWeight: '600' }}>
                    Add New Revenue Adjustment
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '150px 180px 1fr 220px', gap: '10px', alignItems: 'start' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '5px' }}>
                        Amount ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={tutorRevenueForm.amount}
                        onChange={(e) => setTutorRevenueForm({ ...tutorRevenueForm, amount: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                        placeholder="0.00"
                      />
                      <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '11px' }}>
                        Use negative to subtract
                      </small>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '5px' }}>
                        Date
                      </label>
                      <input
                        type="date"
                        value={tutorRevenueForm.date}
                        onChange={(e) => setTutorRevenueForm({ ...tutorRevenueForm, date: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '5px' }}>
                        Note (optional)
                      </label>
                      <input
                        type="text"
                        value={tutorRevenueForm.note}
                        onChange={(e) => setTutorRevenueForm({ ...tutorRevenueForm, note: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                        placeholder="e.g., Additional lesson not in TutorCruncher"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '5px', opacity: 0 }}>
                        Action
                      </label>
                      <button
                        onClick={handleAddTutorRevenueAdjustment}
                        style={{
                          width: '100%',
                          padding: '8px 20px',
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
                      >
                        {editingRevenueAdjustmentIndex !== null ? 'Update Revenue Adjustment' : 'Add Revenue Adjustment'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Add Tutor Modal */}
      {showAddTutorModal && (
        <div className="modal-overlay" onClick={() => setShowAddTutorModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Tutor to {addTutorMarket} Market</h2>
              <button onClick={() => setShowAddTutorModal(false)} className="modal-close">✕</button>
            </div>
            <div className="modal-body">
              {!showCreateManualTutor ? (
                <>
                  <div style={{marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <h3 style={{margin: 0, fontSize: '16px', fontWeight: '600'}}>Search Tutors</h3>
                    <button
                      onClick={() => setShowCreateManualTutor(true)}
                      className="add-result-btn"
                      style={{
                        backgroundColor: '#8b5cf6',
                        padding: '8px 16px',
                        fontSize: '14px'
                      }}
                    >
                      + Create Manual Tutor
                    </button>
                  </div>
                  <div className="search-box">
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search tutors by name or email..."
                      value={tutorSearchQuery}
                      onChange={(e) => setTutorSearchQuery(e.target.value)}
                      className="search-input"
                      autoFocus
                    />
                  </div>
                  <div className="search-results">
                    {tutorSearchResults.length === 0 && tutorSearchQuery.length >= 2 && (
                      <div className="empty-state">
                        <p>No tutors found. Try searching with different terms or create a manual tutor.</p>
                      </div>
                    )}
                    {tutorSearchResults.map((tutor) => (
                      <div key={tutor.id || tutor.contractorId} className="search-result-item">
                        <div className="tutor-info">
                          <div className="tutor-name-search">
                            {tutor.firstName} {tutor.lastName}
                            {tutor.isManual && (
                              <span style={{
                                marginLeft: '8px',
                                padding: '2px 8px',
                                fontSize: '11px',
                                fontWeight: '600',
                                borderRadius: '12px',
                                backgroundColor: '#ede9fe',
                                color: '#6d28d9'
                              }}>
                                Manual
                              </span>
                            )}
                            {tutor.status && tutor.status !== 'active' && (
                              <span className={`status-badge status-${tutor.status}`} style={{
                                marginLeft: '8px',
                                padding: '2px 8px',
                                fontSize: '11px',
                                fontWeight: '600',
                                borderRadius: '12px',
                                textTransform: 'uppercase',
                                backgroundColor: tutor.status === 'dormant' ? '#fef3c7' : 
                                               tutor.status === 'rejected' ? '#fee2e2' : 
                                               tutor.status === 'pending' ? '#dbeafe' : '#e5e7eb',
                                color: tutor.status === 'dormant' ? '#92400e' : 
                                       tutor.status === 'rejected' ? '#991b1b' : 
                                       tutor.status === 'pending' ? '#1e40af' : '#374151'
                              }}>
                                {tutor.status}
                              </span>
                            )}
                          </div>
                          <div className="tutor-email">{tutor.email}</div>
                        </div>
                        <button
                          onClick={() => handleAddTutor(
                            tutor.contractorId || tutor.id?.toString(),
                            `${tutor.firstName} ${tutor.lastName}`,
                            addTutorMarket
                          )}
                          className="add-result-btn"
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div>
                  <div style={{marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <h3 style={{margin: 0, fontSize: '16px', fontWeight: '600'}}>Create Manual Tutor</h3>
                    <button
                      onClick={() => {
                        setShowCreateManualTutor(false);
                        setManualTutorForm({ firstName: '', lastName: '', email: '' });
                      }}
                      className="add-result-btn"
                      style={{
                        backgroundColor: '#6b7280',
                        padding: '8px 16px',
                        fontSize: '14px'
                      }}
                    >
                      ← Back to Search
                    </button>
                  </div>
                  <p style={{marginBottom: '16px', color: '#6b7280', fontSize: '14px'}}>
                    Create a tutor for profit share calculations (e.g., tutors from before TutorCruncher). 
                    These tutors won't sync to TutorCruncher but can be used for manual revenue/tutor pay entries.
                  </p>
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      placeholder="e.g., JP"
                      value={manualTutorForm.firstName}
                      onChange={(e) => setManualTutorForm({ ...manualTutorForm, firstName: e.target.value })}
                      className="form-input"
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      placeholder="e.g., Wohl"
                      value={manualTutorForm.lastName}
                      onChange={(e) => setManualTutorForm({ ...manualTutorForm, lastName: e.target.value })}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Email (Optional)</label>
                    <input
                      type="email"
                      placeholder="e.g., jp.wohl@example.com"
                      value={manualTutorForm.email}
                      onChange={(e) => setManualTutorForm({ ...manualTutorForm, email: e.target.value })}
                      className="form-input"
                    />
                  </div>
                  <div className="modal-actions" style={{marginTop: '20px'}}>
                    <button 
                      onClick={handleCreateManualTutor}
                      className="save-btn"
                      disabled={!manualTutorForm.firstName || !manualTutorForm.lastName}
                    >
                      Create & Add to {addTutorMarket} Market
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Travel Stipend Lesson Modal */}
      {showTravelStipendLessonModal && selectedTravelStipendLessons && selectedTravelStipendTutor && selectedTravelStipendWeek && (
        <div className="modal-overlay" onClick={() => setShowTravelStipendLessonModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📚 Lessons for {selectedTravelStipendTutor}</h2>
              <button onClick={() => setShowTravelStipendLessonModal(false)} className="modal-close">✕</button>
            </div>
            <div className="modal-body">
              <div className="lessons-summary">
                <div className="summary-item">
                  <span>Week Period:</span>
                  <span>{new Date(selectedTravelStipendWeek.weekStart).toLocaleDateString()} - {new Date(selectedTravelStipendWeek.weekEnd).toLocaleDateString()}</span>
                </div>
                <div className="summary-item">
                  <span>Total Hours:</span>
                  <span>{selectedTravelStipendWeek.hours}h</span>
                </div>
                <div className="summary-item">
                  <span>Travel Stipend:</span>
                  <span>{formatCurrency(selectedTravelStipendWeek.stipend)}</span>
                </div>
                <div className="summary-item">
                  <span>Total Lessons:</span>
                  <span>{selectedTravelStipendLessons.length}</span>
                </div>
              </div>
              
              <div className="lessons-list">
                <h3 style={{ marginBottom: '15px', color: '#1e293b', fontSize: '16px' }}>
                  Lessons
                </h3>
                {selectedTravelStipendLessons.map((lesson, index) => (
                  <div key={index} className="lesson-item">
                    <div className="lesson-header">
                      <span className="lesson-date">
                        {new Date(lesson.start).toLocaleDateString()} {new Date(lesson.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="lesson-details">
                      {lesson.topic && (
                        <div><strong>Appointment:</strong> {lesson.topic}</div>
                      )}
                      {lesson.clientName && (
                        <div><strong>Client:</strong> {lesson.clientName}</div>
                      )}
                      {lesson.appointmentId && (
                        <div>
                          <strong>Lesson ID:</strong>{' '}
                          <a 
                            href={`https://account.storytimechess.com/cal/appointments/${lesson.appointmentId}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#3b82f6',
                              textDecoration: 'none',
                              fontWeight: '500'
                            }}
                            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                          >
                            {lesson.appointmentId}
                          </a>
                        </div>
                      )}
                      {lesson.serviceName && (
                        <div><strong>Service:</strong> {lesson.serviceName}</div>
                      )}
                      <div><strong>Duration:</strong> {lesson.units}h</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Office Hours Modal */}
      {showOfficeHoursModal && (
        <div className="modal-overlay" onClick={() => setShowOfficeHoursModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingOfficeHoursIndex !== null ? 'Edit' : 'Add'} Office Hours</h2>
              <button onClick={() => setShowOfficeHoursModal(false)} className="modal-close">✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Tutor Name</label>
                <input
                  type="text"
                  placeholder="e.g., Mafalda Pinto Correia"
                  value={officeHoursForm.tutorName}
                  onChange={(e) => setOfficeHoursForm({ ...officeHoursForm, tutorName: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Hours</label>
                  <input
                    type="number"
                    step="0.5"
                    placeholder="e.g., 3"
                    value={officeHoursForm.hours}
                    onChange={(e) => setOfficeHoursForm({ ...officeHoursForm, hours: e.target.value })}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Hourly Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="e.g., 25.00"
                    value={officeHoursForm.rate}
                    onChange={(e) => setOfficeHoursForm({ ...officeHoursForm, rate: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  placeholder="e.g., Advanced puzzle book work"
                  value={officeHoursForm.description}
                  onChange={(e) => setOfficeHoursForm({ ...officeHoursForm, description: e.target.value })}
                  className="form-input"
                />
              </div>
              {officeHoursForm.hours && officeHoursForm.rate && (
                <div className="calculated-total">
                  Total: {formatCurrency(parseFloat(officeHoursForm.hours) * parseFloat(officeHoursForm.rate))}
                </div>
              )}
              <div className="modal-actions">
                <button onClick={() => setShowOfficeHoursModal(false)} className="cancel-btn">
                  Cancel
                </button>
                <button 
                  onClick={handleAddOfficeHours}
                  className="save-btn"
                  disabled={!officeHoursForm.tutorName || !officeHoursForm.hours || !officeHoursForm.rate}
                >
                  {editingOfficeHoursIndex !== null ? 'Update' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Mileage Modal */}
      {showMileageModal && (
        <div className="modal-overlay" onClick={() => setShowMileageModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingMileageIndex !== null ? 'Edit' : 'Add'} Mileage Reimbursement</h2>
              <button onClick={() => setShowMileageModal(false)} className="modal-close">✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Tutor Name</label>
                <input
                  type="text"
                  placeholder="e.g., Caitlin Goldie"
                  value={mileageForm.tutorName}
                  onChange={(e) => setMileageForm({ ...mileageForm, tutorName: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="e.g., 305.00"
                  value={mileageForm.amount}
                  onChange={(e) => setMileageForm({ ...mileageForm, amount: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  placeholder="e.g., July 2025 mileage reimbursement"
                  value={mileageForm.description}
                  onChange={(e) => setMileageForm({ ...mileageForm, description: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="modal-actions">
                <button onClick={() => setShowMileageModal(false)} className="cancel-btn">
                  Cancel
                </button>
                <button 
                  onClick={handleAddMileage}
                  className="save-btn"
                  disabled={!mileageForm.tutorName || !mileageForm.amount}
                >
                  {editingMileageIndex !== null ? 'Update' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Software License Modal */}
      {showSoftwareLicenseModal && (
        <div className="modal-overlay" onClick={() => setShowSoftwareLicenseModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSoftwareLicenseIndex !== null ? 'Edit' : 'Add'} Software License</h2>
              <button onClick={() => setShowSoftwareLicenseModal(false)} className="modal-close">✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="e.g., 62.54"
                  value={softwareLicenseForm.amount}
                  onChange={(e) => setSoftwareLicenseForm({ ...softwareLicenseForm, amount: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  placeholder="e.g., Zoom + Slack + G Suite - User 1"
                  value={softwareLicenseForm.description}
                  onChange={(e) => setSoftwareLicenseForm({ ...softwareLicenseForm, description: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="modal-actions">
                <button onClick={() => setShowSoftwareLicenseModal(false)} className="cancel-btn">
                  Cancel
                </button>
                <button
                  onClick={handleAddSoftwareLicense}
                  className="save-btn"
                  disabled={!softwareLicenseForm.amount}
                >
                  {editingSoftwareLicenseIndex !== null ? 'Update' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* School Revenue Modal */}
      {showSchoolRevenueModal && (
        <div className="modal-overlay" onClick={() => setShowSchoolRevenueModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSchoolRevenueIndex !== null ? 'Edit' : 'Add'} School Revenue Adjustment</h2>
              <button onClick={() => setShowSchoolRevenueModal(false)} className="modal-close">✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Tutor Name</label>
                <input
                  type="text"
                  placeholder="e.g., Caitlin Goldie"
                  value={schoolRevenueForm.tutorName}
                  onChange={(e) => setSchoolRevenueForm({ ...schoolRevenueForm, tutorName: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Market</label>
                  <select
                    value={schoolRevenueForm.market}
                    onChange={(e) => setSchoolRevenueForm({ ...schoolRevenueForm, market: e.target.value })}
                    className="form-input"
                  >
                    <option value="LA">LA</option>
                    <option value="SF">SF</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="e.g., 500.00"
                    value={schoolRevenueForm.amount}
                    onChange={(e) => setSchoolRevenueForm({ ...schoolRevenueForm, amount: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  placeholder="e.g., Additional school program revenue"
                  value={schoolRevenueForm.description}
                  onChange={(e) => setSchoolRevenueForm({ ...schoolRevenueForm, description: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="modal-actions">
                <button onClick={() => setShowSchoolRevenueModal(false)} className="cancel-btn">
                  Cancel
                </button>
                <button
                  onClick={handleAddSchoolRevenue}
                  className="save-btn"
                  disabled={!schoolRevenueForm.tutorName || !schoolRevenueForm.amount || !schoolRevenueForm.market}
                >
                  {editingSchoolRevenueIndex !== null ? 'Update' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="modal-overlay" onClick={() => setShowSettingsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>⚙️ Profit Share Settings</h2>
              <button onClick={() => setShowSettingsModal(false)} className="modal-close">✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px'}}>
                <div className="form-group">
                  <label>LA Ad Spend</label>
                  <input
                    type="number"
                    step="0.01"
                    value={settingsForm.laAdSpend}
                    onChange={(e) => setSettingsForm({ ...settingsForm, laAdSpend: parseFloat(e.target.value) || 0 })}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>SF Ad Spend</label>
                  <input
                    type="number"
                    step="0.01"
                    value={settingsForm.sfAdSpend}
                    onChange={(e) => setSettingsForm({ ...settingsForm, sfAdSpend: parseFloat(e.target.value) || 0 })}
                    className="form-input"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Caitlin's Salary</label>
                <input
                  type="number"
                  step="0.01"
                  value={settingsForm.caitlinSalary}
                  onChange={(e) => setSettingsForm({ ...settingsForm, caitlinSalary: parseFloat(e.target.value) || 0 })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Profit Share Percentage (%)</label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={settingsForm.profitSharePercentage}
                  onChange={(e) => setSettingsForm({ ...settingsForm, profitSharePercentage: parseFloat(e.target.value) || 0 })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  rows="3"
                  placeholder="Any additional notes for this month..."
                  value={settingsForm.notes}
                  onChange={(e) => setSettingsForm({ ...settingsForm, notes: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="modal-actions">
                <button onClick={() => setShowSettingsModal(false)} className="cancel-btn">
                  Cancel
                </button>
                <button onClick={handleUpdateSettings} className="save-btn">
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Club Lessons Modal */}
      {showClubLessonModal && selectedClubLessons && (
        <div className="modal-overlay" onClick={() => setShowClubLessonModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {selectedClubType === 'lessons' ? '📚' : '🔧'} 
                {selectedClubType === 'lessons' ? 'Lessons' : 'Support'} for {selectedClubLessons.clubName}
              </h2>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button
                  onClick={() => downloadClubCSV(selectedClubLessons.lessons, selectedClubType, selectedClubLessons.clubName)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
                >
                  📥 Download CSV
                </button>
                <button onClick={() => setShowClubLessonModal(false)} className="modal-close">✕</button>
              </div>
            </div>
            <div className="modal-body">
              <div className="lessons-summary">
                <div className="summary-item">
                  <span>Total {selectedClubType === 'lessons' ? 'Lessons' : 'Support Hours'}:</span>
                  <span>{selectedClubLessons.lessons.length}</span>
                </div>
                <div className="summary-item">
                  <span>Total Revenue:</span>
                  <span>{formatCurrency(selectedClubLessons.lessons.reduce((sum, lesson) => sum + lesson.revenue, 0))}</span>
                </div>
                <div className="summary-item">
                  <span>Total Tutor Pay:</span>
                  <span>{formatCurrency(selectedClubLessons.lessons.reduce((sum, lesson) => sum + lesson.tutorPay, 0))}</span>
                </div>
              </div>
              
              <div className="lessons-list">
                <h3 style={{ marginBottom: '15px', color: '#1e293b', fontSize: '16px' }}>
                  {selectedClubType === 'lessons' ? 'Lessons' : 'Support Hours'}
                </h3>
                {selectedClubLessons.lessons.map((lesson, index) => (
                  <div key={index} className="lesson-item">
                    <div className="lesson-header">
                      <span className="lesson-date">
                        {new Date(lesson.start).toLocaleDateString()} {new Date(lesson.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {lesson.tutorsOnLesson > 1 && (
                        <span className="multi-tutor-badge">{lesson.tutorsOnLesson} Tutors</span>
                      )}
                    </div>
                    <div className="lesson-details">
                      {lesson.topic && (
                        <div><strong>Appointment:</strong> {lesson.topic}</div>
                      )}
                      {lesson.tutorNames && lesson.tutorNames.length > 0 && (
                        <div><strong>Tutor{lesson.tutorNames.length > 1 ? 's' : ''}:</strong> {lesson.tutorNames.join(', ')}</div>
                      )}
                      {lesson.appointmentId && (
                        <div>
                          <strong>Lesson ID:</strong>{' '}
                          <a 
                            href={`https://account.storytimechess.com/cal/appointments/${lesson.appointmentId}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#3b82f6',
                              textDecoration: 'none',
                              fontWeight: '500'
                            }}
                            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                          >
                            {lesson.appointmentId}
                          </a>
                        </div>
                      )}
                      {lesson.service_name && (
                        <div><strong>Service:</strong> {lesson.service_name}</div>
                      )}
                      <div><strong>Revenue:</strong> {formatCurrency(lesson.revenue)}</div>
                      <div><strong>Tutor Pay:</strong> {formatCurrency(lesson.tutorPay)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfitShare;

